import Frequency from '../assets/clock.svg'
import ExerciseBike from '../assets/exercise-bike.svg'
import Progression from '../assets/progression.svg'
import Volume from '../assets/volume.svg'
import {
  PulseOximeter,
  SecondsAppAndroid,
  SecondsAppIOS,
} from '../components/affiliates/Cardio'
import { Footnote } from '../components/curriculum/Footnote'
import { FootnoteList } from '../components/curriculum/FootnoteList'
import { Explanation } from '../components/explanation/Explanation'
import { Image } from '../components/image/Image'
import { Layout } from '../components/Layout'

const cardioFrequencyWarning = (
  <Explanation color="pink">
    <div>
      <strong>Warning: </strong>To give your <strong>tendons</strong> time to
      recover, wait <strong>at least</strong> 48 hours between cardio sessions{' '}
      <Footnote link="https://stevenlow.org/overcoming-tendonitis/" />. If you
      want to remain active on your off days, walk outside or around the office
      for an hour.
    </div>
  </Explanation>
)

const Cardio = () => {
  return (
    <Layout>
      <div className={'w90 center module'}>
        <h1 className="huge mb10">Cardio</h1>
        <div>
          <Explanation color="blue">
            <div>
              <strong>Why? </strong> Get happier and live longer{' '}
              <FootnoteList
                links={['https://pubmed.ncbi.nlm.nih.gov/12163425/']}
              />
              . All you need is 5 minutes, three times a week.
            </div>
          </Explanation>

          <div>
            <div className="fr mt40 ml20 mb40">
              <Image width={60} height={60} alt="Fan bike" src={ExerciseBike} />
              <div className="ml20">Get access to a bike</div>
            </div>
          </div>
          <Explanation color="blue">
            <div>
              <strong>Why: </strong> Cycling is just as effective as running or
              the elliptical{' '}
              <FootnoteList
                links={[
                  'https://well.blogs.nytimes.com/2013/08/29/ask-well-is-it-better-to-bike-or-run/',
                  'https://www.nytimes.com/2019/04/03/well/move/heart-health-swimming-running-exercise.html',
                  'https://pubmed.ncbi.nlm.nih.gov/15470311/',
                ]}
              />{' '}
              but without joint impact{' '}
              <Footnote link="https://academic.oup.com/ije/article/39/2/580/679411" />{' '}
              or shear stress on your patellar tendon respectively{' '}
              <FootnoteList
                links={[
                  'https://www.livestrong.com/article/405303-knee-pain-on-the-elliptical-trainer/',
                  'https://www.bostonsportsmedicine.com/pdf/protocols/patellar_tendinitis_and_chondromalacia.pdf',
                ]}
              />
              . It also doesn
              {`'t`} interfere with weight lifting{' '}
              <FootnoteList
                links={[
                  'https://pubmed.ncbi.nlm.nih.gov/26932769/',
                  'https://www.strongerbyscience.com/cardio-hypertrophy/',
                  'https://pubmed.ncbi.nlm.nih.gov/30341593/',
                ]}
              />
              . In fact, with the increased mitochondria and a better
              cardiovascular system you get from cardio, you can lift more
              without getting tired{' '}
              <FootnoteList
                links={[
                  'https://breakingmuscle.com/fitness/cardio-doesnt-kill-gains-and-you-should-be-doing-it',
                  'https://www.strongerbyscience.com/avoiding-cardio-could-be-holding-you-back/',
                ]}
              />
              .
            </div>
          </Explanation>
          <Explanation color="green">
            <div>
              <strong>Tip: </strong> To avoid knee injures, adjust the seat.
              There should be a slight (30 degree) bend in your knee when the
              pedal is at the bottom.
            </div>
          </Explanation>

          <div>
            <div className="fr mt40 ml20 mb40">
              <Image width={60} height={60} alt="Weights" src={Volume} />
              <div className="ml20">
                <div className="bold">Volume</div>
                <div>See below</div>
              </div>
            </div>
            <Explanation color="blue">
              <div>
                <strong>Why: </strong>
                High-intensity exercise (HIIT) produces the same results as
                traditional cardio but in way less time; 30 seconds of HIIT is
                equivalent to 30 minutes of traditional cardio{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/22124524/',
                    'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0154075',
                  ]}
                />
                . HIIT also doesn{`'`}t interfere with strength training{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/26932769/" />.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Protocol: </strong> Download the Seconds app on{' '}
                <SecondsAppIOS text="iOS" /> and{' '}
                <SecondsAppAndroid text="Android" /> to keep track of time. Set
                the bike to a high resistance. Peddle gently for two minutes to
                warm up. Then, peddle as hard as you can for{' '}
                <strong>15 seconds</strong>. Then, peddle gently for two minutes
                to rest. Then, do one more 15 second burst and you{`'`}
                re done!
              </div>
            </Explanation>
            <Explanation color="pink">
              <div>
                <strong>Warning:</strong> Don{`'`}t exceed two, 15 seconds
                sprints, as that will put additional stress on your tendons and
                ligaments without additional, cardiovascular benefit{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/22124524/" />.
              </div>
            </Explanation>

            <div>
              <div className="fr mt40 ml20 mb40">
                <Image width={60} height={60} alt="Clock" src={Frequency} />
                <div className="ml20">
                  <div className="bold">Frequency</div>
                  <div>Cycle 2-3 times per week</div>
                </div>
              </div>
              {cardioFrequencyWarning}
            </div>

            <div>
              <div className="fr mt40 ml20 mb40">
                <Image
                  width={60}
                  height={60}
                  alt="Graph with line going up"
                  src={Progression}
                />
                <div className="ml20">
                  <div className="bold">Progression</div>
                  <div>Pedal harder next time!</div>
                </div>
              </div>
              <Explanation color="green">
                <div>
                  <strong>Tip: </strong> To measure your progress, buy a $15{' '}
                  <PulseOximeter text="pulse oximeter" /> and measure the change
                  in your resting heart rate each week. You want to see a
                  downward trend each month, ending in the 50s{' '}
                  <Footnote link="https://www.strongerbyscience.com/avoiding-cardio-could-be-holding-you-back/" />
                  .
                </div>
              </Explanation>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Cardio
